import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUser, FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { ReactComponent as Logo } from '../assets/icons/Logo.svg';
import SecundarabadSlide1 from '../assets/images/SecundarabadSlide1.png';
import SecundarabadSlide2 from '../assets/images/SecundarabadSlide2.png';
import SecundarabadSlide3 from '../assets/images/SecundarabadSlide3.png';
import SplitSlideshow from '../components/global/SplitSlideshow';
import layoutStyles from '../styles/AuthLayout.module.css';
import './Login.css';

const slides = [
  {
    title: 'Railway Installation',
    img: SecundarabadSlide1,
    description:
      'Track installation progress, validate submissions, and manage field teams in one place.',
  },
  {
    title: 'Feature Highlight',
    img: SecundarabadSlide2,
    description:
      'Review pending verifications, accept/reject, and keep operations moving.',
  },
  {
    title: 'Stay Connected',
    img: SecundarabadSlide3,
    description: 'Secure access, faster workflows, and a clearer view of operations.',
  },
];

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (showForgotPassword) {
      if (!email || !email.includes('@')) {
        setError('Please enter a valid email address');
        return;
      }
      // UI-only placeholder (does not touch auth flow)
      setError('Password reset link sent to your email!');
      return;
    }

    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password');
      return;
    }

    setLoading(true);
    try {
      const result = await login(username, password);
      if (result.success) {
        navigate('/', { replace: true });
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={layoutStyles.container}>
      <div className={layoutStyles.left} aria-hidden="true">
        <SplitSlideshow slides={slides} showDots autoPlayInterval={4000} />
      </div>

      <div className={layoutStyles.right}>
        <div className={layoutStyles.rightHeader}>
          <div className="biLogin_container">
            <div className="biLogin_header_container" aria-hidden="true">
              <div className="biLogin_logo">
                <Logo width={60} height={60} aria-label="Company logo" />
              </div>
            </div>

            <form className="biLogin_form" onSubmit={handleSubmit}>
              {error ? (
                <div
                  className={`biLogin_error_message ${
                    error.toLowerCase().includes('sent') ? 'isSuccess' : ''
                  }`}
                  role="alert"
                >
                  <div className="biLogin_error_content">
                    <div className="biLogin_error_text">
                      <p>{error}</p>
                    </div>
                    <button
                      type="button"
                      className="biLogin_error_close"
                      onClick={() => setError('')}
                      aria-label="Dismiss message"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ) : null}

              {showForgotPassword ? (
                <>
                  <div className="biLogin_input_container biLogin_password_input_wrapper">
                    <input
                      type="email"
                      placeholder="Email Address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                    />
                    <span className="biLogin_trail_icon" aria-hidden="true">
                      <FiUser size={18} />
                    </span>
                  </div>

                  <div className="biLogin_forgot_actions">
                    <div className="biLogin_forgot_action_item">
                      <button
                        type="button"
                        className="biLogin_btn biLogin_btn_primary"
                        onClick={() => {
                          setShowForgotPassword(false);
                          setError('');
                        }}
                      >
                        Back to Login
                      </button>
                    </div>
                    <div className="biLogin_forgot_action_item">
                      <button
                        type="submit"
                        className="biLogin_btn biLogin_btn_secondary"
                        disabled={loading}
                      >
                        {loading ? 'Sending...' : 'Send verification code'}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="biLogin_input_container biLogin_password_input_wrapper">
                    <input
                      type="text"
                      placeholder="Username or Email Address"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="username"
                    />
                    <span className="biLogin_trail_icon" aria-hidden="true">
                      <FiUser size={18} />
                    </span>
                  </div>

                  <div className="biLogin_input_container biLogin_password_input_wrapper">
                    <input
                      type={showPw ? 'text' : 'password'}
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="biLogin_password_toggle"
                      onClick={() => setShowPw((v) => !v)}
                      aria-label={showPw ? 'Hide password' : 'Show password'}
                    >
                      {showPw ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>

                  <div className="biLogin_remember_forgot_cont">
                    <label className="biLogin_checkbox">
                      <input
                        type="checkbox"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                      />
                      Remember Me
                    </label>
                    <button
                      type="button"
                      className="biLogin_link_button"
                      onClick={() => {
                        setShowForgotPassword(true);
                        setEmail('');
                        setError('');
                      }}
                    >
                      Forgot Password?
                    </button>
                  </div>

                  <div className="biLogin_login_btn_cont">
                    <button
                      type="submit"
                      className="biLogin_btn biLogin_btn_secondary"
                      disabled={loading}
                    >
                      {loading ? 'Logging in...' : 'Login'}
                    </button>
                  </div>
                </>
              )}

              <div className="biLogin_back_row">
                <button type="button" className="biLogin_back_button" onClick={() => navigate(-1)}>
                  Back
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
